import { Link, useNavigate } from "@tanstack/react-router";
import { NotificationPrompt } from "@web/components/NotificationPrompt";
import { ArrowRight, cardShadow, surfaceBase } from "@web/components/ui";
import { usePushNotifications } from "@web/hooks/usePushNotifications";
import { useCallback, useState } from "react";
import { ActiveCompetitionsSkeleton, SkeletonBox, StatsBarSkeleton } from "@/components/skeleton";
import { Pill } from "@/components/ui";
import { useAuth } from "@/contexts";
import { useDashboardData } from "@/hooks/useDashboardData";
import { ACCENT_COLORS, PAST_ACCENT_COLORS } from "@/theme";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    currentUser,
    pendingInvites,
    loading,
    ghConnected,
    ahConnected: appleHealthConnected,
    userName,
    activeCompetitions,
    pastCompetitions,
  } = useDashboardData();
  const [pushDismissed, setPushDismissed] = useState(() => {
    try {
      return localStorage.getItem("push-prompt-dismissed") === "true";
    } catch {
      return false;
    }
  });
  const push = usePushNotifications();

  const handleEnablePush = useCallback(async () => {
    await push.subscribe();
    setPushDismissed(true);
    try {
      localStorage.setItem("push-prompt-dismissed", "true");
    } catch {
      /* noop */
    }
  }, [push]);

  const handleDismissPush = useCallback(() => {
    setPushDismissed(true);
    try {
      localStorage.setItem("push-prompt-dismissed", "true");
    } catch {
      /* noop */
    }
  }, []);

  const accentColors = ACCENT_COLORS;
  const pastAccentColors = PAST_ACCENT_COLORS;

  return (
    <div className="min-h-screen bg-background relative">
      <div
        className="fixed -top-[7.5rem] -right-[5rem] w-[18.75rem] h-[18.75rem] rounded-full bg-[rgba(32,191,107,0.08)] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="fixed top-[12.5rem] -left-[3.75rem] w-[12.5rem] h-[12.5rem] rounded-full bg-[rgba(156,77,255,0.07)] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="fixed bottom-[5rem] -right-[2.5rem] w-[8.75rem] h-[8.75rem] rounded-full bg-[rgba(56,182,255,0.07)] pointer-events-none"
        aria-hidden="true"
      />
      <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 min-h-screen max-w-3xl mx-auto w-full">
        <div className="flex flex-col gap-3 md:flex-row md:gap-4 md:items-stretch">
          <div className={`${surfaceBase} bg-card-strong p-6 overflow-hidden md:flex-1`}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-aqua border border-text" />
                <span className="text-text font-black text-[0.8125rem] uppercase tracking-[0.0625em]">
                  Welcome back
                </span>
              </div>
              {currentUser?.orgId && (
                <button
                  type="button"
                  onClick={() => navigate({ to: "/org" })}
                  className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
                >
                  <span className="text-purple text-[0.8125rem] font-bold">Admin</span>
                </button>
              )}
            </div>
            {loading ? (
              <SkeletonBox width={200} height={40} borderRadius={8} />
            ) : (
              <div className="flex items-center gap-3 mt-1">
                <div className="w-11 h-11 rounded-full border border-text flex items-center justify-center overflow-hidden shrink-0 bg-[rgba(156,77,255,0.1)]">
                  {user?.image ? (
                    <img
                      src={user.image}
                      alt=""
                      className="w-11 h-11 object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-purple text-[1.125rem] font-black">
                      {userName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="text-text text-[2.25rem] font-black tracking-[-0.0625em] leading-[2.625rem] md:text-[2.75rem] md:leading-[3.125rem]">
                  {userName}
                </span>
              </div>
            )}
            <span className="text-text-muted text-[1rem] leading-6 max-w-[35rem] block mt-2">
              Track this week's movement, jump into active competitions, and keep friend activity in
              view.
            </span>
          </div>
          <div className={`${surfaceBase} bg-white p-5 md:w-[15rem] md:gap-0 md:justify-center`}>
            {loading ? (
              <StatsBarSkeleton />
            ) : (
              <div className="flex md:flex-col md:divide-y md:divide-[rgba(28,27,46,0.1)]">
                <div className="flex-1 flex flex-col items-center gap-1 py-3 md:py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-[1.125rem]" role="img" aria-label="Active competitions">
                      🏆
                    </span>
                    <span className="text-text text-[1.375rem] font-black">
                      {activeCompetitions.length}
                    </span>
                  </div>
                  <span className="text-text-dim text-[0.75rem] font-bold uppercase">Active</span>
                </div>
                <div className="w-[0.125rem] bg-[rgba(28,27,46,0.1)] md:hidden" />
                <div className="flex-1 flex flex-col items-center gap-1 py-3 md:py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-[1.125rem]" role="img" aria-label="Pending invites">
                      ✉️
                    </span>
                    <span className="text-text text-[1.375rem] font-black">
                      {pendingInvites.length}
                    </span>
                  </div>
                  <span className="text-text-dim text-[0.75rem] font-bold uppercase">Invites</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2.5">
          <Link
            to="/competition/create"
            className={`flex-1 bg-aqua rounded-[20px] py-4 px-5 text-center no-underline ${cardShadow}`}
          >
            <span className="text-white font-extrabold text-[0.9375rem]">Launch a competition</span>
          </Link>
          <Link
            to="/join"
            className={`bg-white rounded-[20px] py-4 px-5 text-center no-underline ${cardShadow}`}
          >
            <span className="text-text font-extrabold text-[0.9375rem]">Join by code</span>
          </Link>
          {!user && (
            <Link
              to="/sign-in"
              className={`bg-white rounded-[20px] py-4 px-5 border border-text text-center no-underline ${cardShadow}`}
            >
              <span className="text-text font-extrabold text-[0.9375rem]">Sign in</span>
            </Link>
          )}
        </div>

        <div className="gap-5 md:flex md:gap-6 md:items-start">
          <div className="gap-4 md:flex-1 md:min-w-0">
            <div className={`${surfaceBase} bg-card p-4`}>
              <div className="flex justify-between items-center">
                <span className="text-text text-[1.25rem] font-extrabold tracking-[-0.0187em]">
                  Active competitions
                </span>
                <span className="text-text text-[1.25rem] font-extrabold tracking-[-0.0187em]">
                  {activeCompetitions.length}
                </span>
              </div>
              <div className="mt-4">
                {loading && <ActiveCompetitionsSkeleton />}
                {!loading && activeCompetitions.length === 0 && (
                  <div className={`${surfaceBase} bg-white p-4`}>
                    <span className="text-text-muted text-[0.875rem]">
                      No active competitions yet.
                    </span>
                    <Link to="/competition/create" className="no-underline mt-2 block">
                      <span className="text-aqua text-[0.875rem] font-bold">Launch one →</span>
                    </Link>
                  </div>
                )}
                {activeCompetitions.map((competition, idx) => {
                  const accent = accentColors[idx % accentColors.length];
                  const avatars = competition.leaderboard?.slice(0, 4) ?? [];
                  const extraCount = (competition.memberCount ?? 0) - avatars.length;
                  return (
                    <Link
                      key={competition.id}
                      to={`/competition/${competition.id}`}
                      className={`${surfaceBase} bg-white overflow-hidden no-underline flex mb-2`}
                    >
                      <div className="flex-1 p-4 gap-1.5 flex flex-col min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="text-text text-[1.0625rem] font-black line-clamp-2"
                            title={competition.name}
                          >
                            {competition.name}
                          </span>
                          {competition.scopeMode && (
                            <Pill
                              label={
                                competition.scopeMode === "group"
                                  ? "Group only"
                                  : competition.scopeMode === "group_cross_office"
                                    ? "Cross-office"
                                    : "Cross-group"
                              }
                              tone="accent"
                            />
                          )}
                          <div
                            className={`shrink-0 ml-auto rounded-full px-2.5 py-1 text-white text-[0.6875rem] font-black ${cardShadow}`}
                            style={{ backgroundColor: accent }}
                          >
                            {competition.memberCount} members
                          </div>
                        </div>
                        <span className="text-text-muted text-[0.8125rem] inline-flex items-center gap-1">
                          {competition.startsAt} <ArrowRight /> {competition.endsAt}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className="flex">
                            {avatars.map((entry, i) => (
                              <div
                                key={entry.userId}
                                className={`w-7 h-7 rounded-full border border-text flex items-center justify-center ${entry.avatarUrl ? "overflow-hidden" : ""}`}
                                style={{
                                  backgroundColor: entry.avatarUrl ? "transparent" : accent,
                                  marginLeft: i > 0 ? -8 : 0,
                                  zIndex: avatars.length - i,
                                  position: "relative",
                                }}
                              >
                                {entry.avatarUrl ? (
                                  <img
                                    src={entry.avatarUrl}
                                    alt=""
                                    className="w-7 h-7 object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <span className="text-white text-[0.6875rem] font-black">
                                    {entry.name?.charAt(0)?.toUpperCase() ?? "?"}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                          {extraCount > 0 && (
                            <span className="text-text-dim text-[0.75rem] font-bold">
                              +{extraCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {pendingInvites.length > 0 && (
              <>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-text text-[1.25rem] font-black tracking-[-0.0187em]">
                    Active invites
                  </span>
                  <span className="text-text-dim text-[0.8125rem] font-bold">
                    {pendingInvites.length}
                  </span>
                </div>
                {pendingInvites.map((invite) => (
                  <Link
                    key={invite.competitionId + invite.inviteCode}
                    to={`/join/${invite.inviteCode}`}
                    className={`${surfaceBase} bg-card overflow-hidden no-underline flex mb-2`}
                  >
                    <div className="w-2 bg-purple border-r-2 border-r-text" />
                    <div className="flex-1 p-4 gap-1 flex flex-col">
                      <span className="text-text text-[1rem] font-black">
                        {invite.competitionName}
                      </span>
                      <span className="text-text-muted text-[0.8125rem]">
                        Invited by {invite.invitedBy}
                      </span>
                      <span className="text-text-muted text-[0.75rem] inline-flex items-center gap-1">
                        {invite.startsAt} <ArrowRight /> {invite.endsAt}
                      </span>
                      <div
                        className={`bg-purple rounded-full px-2.5 py-1 self-start mt-1 ${cardShadow}`}
                      >
                        <span className="text-white text-[0.75rem] font-black">Tap to join</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </>
            )}

            {pastCompetitions.length > 0 && (
              <>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-text text-[1.25rem] font-black tracking-[-0.0187em]">
                    Past competitions
                  </span>
                  <span className="text-text-dim text-[0.8125rem] font-bold">
                    {pastCompetitions.length}
                  </span>
                </div>
                {pastCompetitions.map((competition, idx) => {
                  const accent = pastAccentColors[idx % pastAccentColors.length];
                  return (
                    <Link
                      key={competition.id}
                      to={`/competition/${competition.id}`}
                      className={`${surfaceBase} bg-card overflow-hidden no-underline flex mb-2 opacity-[0.65]`}
                    >
                      <div className="flex-1 p-4 gap-1.5 flex flex-col min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="text-text text-[1.0625rem] font-black line-clamp-2"
                            title={competition.name}
                          >
                            {competition.name}
                          </span>
                          <div
                            className={`shrink-0 ml-auto rounded-full px-2.5 py-1 text-text text-[0.6875rem] font-black ${cardShadow}`}
                            style={{ backgroundColor: accent }}
                          >
                            Ended
                          </div>
                        </div>
                        <span className="text-text-muted text-[0.8125rem] inline-flex items-center gap-1">
                          {competition.startsAt} <ArrowRight /> {competition.endsAt}
                        </span>
                        <span className="text-text-dim text-[0.8125rem] font-black">
                          Code: {competition.inviteCode}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </>
            )}
          </div>

          <div className="gap-5 md:w-[23.75rem] md:flex-shrink-0">
            {push.status === "default" && !pushDismissed && (
              <NotificationPrompt
                onEnable={handleEnablePush}
                onDismiss={handleDismissPush}
                loading={false}
              />
            )}
            {!ghConnected && !appleHealthConnected && (
              <button
                type="button"
                onClick={() => navigate({ to: "/settings", hash: "health-connections" })}
                className={`${surfaceBase} bg-card p-4 flex flex-col gap-1 cursor-pointer text-left w-full ${cardShadow}`}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-aqua"
                >
                  <title>Connect a health provider</title>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <span className="text-text text-[0.9375rem] font-bold">
                  Connect a health provider
                </span>
                <span className="text-text-muted text-[0.8125rem]">
                  Sync your steps and start competing with friends.
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
